import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNotaire } from "../../context/NotaireContext";
import {
  CalendarDaysIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import EmptyState from "../../components/EmptyState";
import { Heading } from "../../components/catalyst/heading";
import googleCalendaricn from "../../img/google-calendar-icn.svg";
import { CheckCircleIcon } from "@heroicons/react/16/solid";
import { AlertMessage } from "../../components/MessageAlert";
import PanelPlagesDisponibilites from "../fiche_notaire/PanelPlagesDisponibilites";
import IndisponibiliteNotaire from "../../components/PanelNotaireIndisponibilites";

//prod api : https://cloterenotaire.onrender.com
//local : http://localhost:5050

import { API_BASE } from "../../config";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const AgendaSyncButton = () => {
  const { user } = useNotaire();
  const airtableId = user?.airtable_id;
  // const airtableId = "recZWx6ldHoY6Dp1l"

  const [agendaType, setAgendaType] = useState(null);
  const [agendaSynchro, setAgendaSynchro] = useState(false);

  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState([]);
  const [events, setEvents] = useState([]);
  const [hasRealCalendar, setHasRealCalendar] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [showSwitchProvider, setShowSwitchProvider] = useState(false);

  const [activeTab, setActiveTab] = useState("calendrier");

  const tabs = [
    { name: "✅ Je synchronise mon agenda", key: "calendrier" },
    { name: "❌ Je ne synchronise pas mon agenda", key: "horaires" }
  ];

  const handleSyncClick = (provider) => {
    const redirectUri = "https://notaire.clotere.fr/app/calendrier";
    window.location.href = `${API_BASE}/auth/${provider}?airtableUserId=${airtableId}&redirectUri=${encodeURIComponent(
      redirectUri
    )}`;
  };

  const checkSync = async () => {
    if (!airtableId) return;
    try {
      const res = await axios.get(`${API_BASE}/api/sync/status/${airtableId}`);
      if (res.data.provider) {
        setAgendaType(res.data.provider);
        setAgendaSynchro(true);
      } else {
        setAgendaType(null);
        setAgendaSynchro(false);
      }
    } catch (err) {
      console.error("checkSync error:", err);
      setAgendaType(null);
      setAgendaSynchro(false);
    }
  };

  useEffect(() => {
    checkSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airtableId]);

  const fetchSlots = async () => {
    if (!agendaType || !airtableId) return;

    try {
      setLoadingSlots(true);
      const res = await axios.get(`${API_BASE}/api/slots/${airtableId}`, {
        timeout: 20000,
      });

      setSlots(res.data.slots || []);
      setBusy(res.data.busy || []);
      setEvents(res.data.events || []);
      setHasRealCalendar(res.data.hasRealCalendar === true);
      setDiag(res.data.diag || null);
    } catch (err) {
      console.error("fetchSlots error:", err?.response?.data || err.message);
      setSlots([]);
      setBusy([]);
      setEvents([]);
      setHasRealCalendar(false);
      setDiag("FRONT_FETCH_ERROR");
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaType, airtableId]);

  const syncDiagnosis = useMemo(() => {
    if (!agendaSynchro) return null;

    if (loadingSlots) {
      return {
        type: "info",
        title: "Vérification en cours",
        message: "Nous vérifions l’accès à votre agenda…",
      };
    }

    if (hasRealCalendar === false) {
      return {
        type: "error",
        title: "Accès à l’agenda non confirmé",
        message:
          "Nous n’arrivons pas à récupérer votre calendrier. Cliquez sur “Re-tester” ou relancez la synchronisation.",
      };
    }

    if (hasRealCalendar === true && busy.length === 0 && events.length === 0) {
      return {
        type: "success",
        title: "Synchronisation opérationnelle",
        message:
          "Accès confirmé. Aucun événement trouvé sur les 20 prochains jours (ou calendrier vide).",
      };
    }

    return {
      type: "success",
      title: "Synchronisation de l’agenda opérationnelle",
      message:
        "Votre agenda est bien pris en compte pour calculer les créneaux.",
    };
  }, [agendaSynchro, loadingSlots, hasRealCalendar, busy.length, events.length]);

  const resyncLabel =
    agendaType === "google" ? "Re-synchroniser Google" : "Re-synchroniser Outlook";
  const resyncProvider = agendaType === "google" ? "google" : "outlook";

  return (
    <div className="container">
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Votre calendrier</Heading>
      </div>

      {/* Tabs */}
        <div className="grid grid-cols-1 sm:hidden">
          <select
            value={tabs.find((tab) => tab.key === activeTab)?.name}
            onChange={(e) => {
              const selected = tabs.find((tab) => tab.name === e.target.value);
              if (selected) setActiveTab(selected.key);
            }}
            aria-label="Sélectionner un onglet"
            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          >
            {tabs.map((tab) => (
              <option key={tab.key}>{tab.name}</option>
            ))}
          </select>

          <ChevronDownIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
          />
        </div>

        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8 justify-center">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  aria-current={activeTab === tab.key ? "page" : undefined}
                  className={classNames(
                    activeTab === tab.key
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                    "border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap"
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      

      {/* Contenu onglet je synchronise mon agenda */}
      {activeTab === "calendrier" && (
        <>
          <br />

          <AlertMessage
            type="info"
            message="Les créneaux proposés aux utilisateurs sont de 30min, du lundi au vendredi de 9h à 12h et de 14h à 18h."
          />

          <div className="flex flex-col gap-4">
            <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow-sm">
              <div className="p-3">
                <h3 className="text-base font-semibold leading-7 text-gray-900">
                  Synchronisation de votre agenda
                </h3>
              </div>

              <div className="p-4">
                {agendaSynchro ? (
                  <>
                    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
                      <div className="p-3 flex flex-row gap-2 items-center justify-start border border-gray-100 rounded-lg">
                        {agendaType === "google" ? (
                          <>
                            <img
                              src={googleCalendaricn}
                              className="size-12"
                              alt="google calendar"
                            />
                            <div className="flex-1">
                              <p className="mb-0 text-base font-bold">
                                Agenda Google synchronisé
                              </p>
                              <p className="mb-0 text-base">{user.email}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <CalendarDaysIcon className="size-12" />
                            <div className="flex-1">
                              <p className="mb-0 text-base font-bold">
                                Agenda Outlook synchronisé
                              </p>
                              <p className="mb-0 text-base">{user.email}</p>
                            </div>
                          </>
                        )}
                        <div className="flex justify-end">
                          <CheckCircleIcon className="size-6 text-emerald-500" />
                        </div>
                      </div>
                    </div>

                    {syncDiagnosis && (
                      <div className="mt-3">
                        <AlertMessage
                          type={syncDiagnosis.type}
                          message={
                            <>
                              <strong>{syncDiagnosis.title}</strong>
                              <br />
                              {syncDiagnosis.message}
                            </>
                          }
                        />
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={fetchSlots}
                        className="px-3 py-2 rounded-md bg-zinc-900 text-white text-sm"
                        disabled={loadingSlots}
                      >
                        {loadingSlots ? "Re-test…" : "Re-tester"}
                      </button>

                      <button
                        onClick={() => handleSyncClick(resyncProvider)}
                        className={`px-3 py-2 rounded-md text-white text-sm ${
                          agendaType === "google" ? "bg-red-500" : "bg-blue-600"
                        }`}
                      >
                        {resyncLabel}
                      </button>

                      <button
                        onClick={() => setShowSwitchProvider((v) => !v)}
                        className="px-3 py-2 rounded-md bg-gray-100 text-gray-900 text-sm"
                      >
                        {showSwitchProvider ? "Fermer" : "Changer de provider"}
                      </button>
                    </div>

                    {showSwitchProvider && (
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => handleSyncClick("google")}
                          className="px-3 py-2 rounded-md bg-red-500 text-white text-sm"
                        >
                          Synchroniser Google
                        </button>
                        <button
                          onClick={() => handleSyncClick("outlook")}
                          className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm"
                        >
                          Synchroniser Outlook
                        </button>
                      </div>
                    )}

                    {/*
                    {process.env.NODE_ENV !== "production" && (
                      <pre className="mt-3 text-xs bg-gray-50 p-2 rounded border">
                        {JSON.stringify(
                          {
                            agendaType,
                            hasRealCalendar,
                            diag,
                            busy: busy.length,
                            events: events.length,
                            slots: slots.length,
                          },
                          null,
                          2
                        )}
                      </pre>
                    )}
                    */}
                  </>
                ) : (
                  <>
                    <EmptyState
                      icon={ExclamationCircleIcon}
                      title="Synchronisez votre agenda"
                      description="Pour afficher vos disponibilités vous devez synchroniser votre agenda."
                    />
                    <div className="flex justify-center gap-3 mt-2">
                      <button
                        onClick={() => handleSyncClick("google")}
                        className="p-2 bg-red-500 text-white rounded-full"
                      >
                        Synchroniser mon agenda Google
                      </button>
                      <button
                        onClick={() => handleSyncClick("outlook")}
                        className="p-2 bg-blue-500 text-white rounded-full"
                      >
                        Synchroniser mon agenda Outlook
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {agendaSynchro && (
              <div className="mt-4 rounded-lg bg-white shadow-sm p-4">
                <h3 className="text-base font-semibold leading-7 text-gray-900 mb-2">
                  Créneaux occupés (20 prochains jours)
                </h3>

                {busy.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Aucun créneau occupé détecté (ou calendrier vide / accès non
                    confirmé).
                  </p>
                ) : (
                  <ul className="list-disc pl-5 space-y-1">
                    {busy.map((b, idx) => {
                      const start = new Date(b.start);
                      const end = new Date(b.end);

                      const startStr = start.toLocaleString("fr-FR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      });

                      const sameDay =
                        start.getFullYear() === end.getFullYear() &&
                        start.getMonth() === end.getMonth() &&
                        start.getDate() === end.getDate();

                      const endStr = sameDay
                        ? end.toLocaleTimeString("fr-FR", {
                            timeStyle: "short",
                          })
                        : end.toLocaleString("fr-FR", {
                            dateStyle: "short",
                            timeStyle: "short",
                          });

                      return (
                        <li key={idx} className="text-sm text-gray-700">
                          Occupé — {startStr} → {endStr}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Contenu onglet je ne synchronise pas mon agenda */}
      {activeTab === "horaires" && (
        <div className="mt-6">
          <PanelPlagesDisponibilites />
        </div>
      )}
<IndisponibiliteNotaire />

      
    </div>
  );
};

export default AgendaSyncButton;